import { useState } from 'react';
import { CreditCard, FileSignature, KeyRound, Pencil, Plus, UserX, Users } from 'lucide-react';
import { AssignmentCreateModal } from '../components/forms/AssignmentModals';
import { ContractCreateModal } from '../components/forms/ContractModal';
import { DriverFormModal } from '../components/forms/DriverFormModal';
import { Badge, ExpiryBadge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Card, EmptyState, PageHeader } from '../components/ui/Card';
import { DataTable, type Column } from '../components/ui/DataTable';
import { FilterBar, SearchInput } from '../components/ui/FilterBar';
import { Select } from '../components/ui/Input';
import { ConfirmDialog } from '../components/ui/Modal';
import { useDrivers, useUpdateDriver } from '../hooks/useApi';
import { toInt, useUrlFilters } from '../hooks/useUrlFilters';
import { errorMessage } from '../services/api';
import { toast } from '../store';
import type { Driver } from '../types';
import { daysUntil, EMPLOYMENT_LABELS, formatDate } from '../utils/helpers';
import { t } from '../i18n';

export function Drivers() {
  const [filters, setFilters] = useUrlFilters({ search: '', active: 'true', license: '', page: '1', page_size: '20' });
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Driver | null>(null);
  const [toggling, setToggling] = useState<Driver | null>(null);
  const [assigningTo, setAssigningTo] = useState<Driver | null>(null);
  const [contractFor, setContractFor] = useState<Driver | null>(null);
  const update = useUpdateDriver();

  const page = toInt(filters.page, 1)!;
  const pageSize = toInt(filters.page_size, 20)!;
  const { data, isLoading, isFetching } = useDrivers({
    search: filters.search,
    is_active: filters.active === '' ? undefined : filters.active === 'true',
    license_expiring: filters.license === 'expiring' || undefined,
    page,
    page_size: pageSize,
  });

  const columns: Column<Driver>[] = [
    {
      key: 'name',
      header: t('Meno'),
      render: (d) => (
        <div>
          <p className="font-medium">
            {d.last_name} {d.first_name}
          </p>
          <p className="text-xs text-secondary-500">
            {[d.employment_type && EMPLOYMENT_LABELS[d.employment_type], d.employee_id && t('os. č. {employeeId}', { employeeId: d.employee_id }), d.ico && `IČO ${d.ico}`]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </div>
      ),
    },
    { key: 'department', header: t('Oddelenie'), render: (d) => d.department || '–' },
    {
      key: 'contact',
      header: t('Kontakt'),
      render: (d) => (
        <div className="text-sm">
          {d.phone && <a href={`tel:${d.phone}`} className="block hover:underline">{d.phone}</a>}
          {d.email && <a href={`mailto:${d.email}`} className="block text-primary-700 hover:underline">{d.email}</a>}
          {!d.phone && !d.email && '–'}
        </div>
      ),
    },
    {
      key: 'license',
      header: t('Vodičský preukaz'),
      className: 'whitespace-nowrap',
      render: (d) => (
        <div className="text-sm">
          {d.license_number && <p className="font-mono text-xs">{d.license_number}</p>}
          {d.license_expiry ? <ExpiryBadge days={daysUntil(d.license_expiry)} date={`do ${formatDate(d.license_expiry)}`} /> : !d.license_number && '–'}
        </div>
      ),
    },
    { key: 'status', header: t('Stav'), render: (d) => (d.is_active ? <Badge variant="success">{t('Aktívny')}</Badge> : <Badge>{t('Neaktívny')}</Badge>) },
    {
      key: 'actions',
      header: <span className="sr-only">{t('Akcie')}</span>,
      className: 'text-right whitespace-nowrap',
      render: (d) => (
        <>
          {d.is_active && (
            <>
              <button className="icon-btn" onClick={() => setContractFor(d)} title={t('Generovať zmluvu')} aria-label={t('Generovať zmluvu')}>
                <FileSignature className="h-4 w-4" />
              </button>
              <button className="icon-btn" onClick={() => setAssigningTo(d)} title={t('Prideliť vozidlo')} aria-label={t('Prideliť vozidlo')}>
                <KeyRound className="h-4 w-4" />
              </button>
            </>
          )}
          <button className="icon-btn" onClick={() => setEditing(d)} title={t('Upraviť')} aria-label={t('Upraviť')}>
            <Pencil className="h-4 w-4" />
          </button>
          <button
            className={d.is_active ? 'icon-btn-danger' : 'icon-btn'}
            onClick={() => setToggling(d)}
            title={d.is_active ? t('Deaktivovať') : t('Aktivovať')}
            aria-label={d.is_active ? t('Deaktivovať') : t('Aktivovať')}
          >
            <UserX className="h-4 w-4" />
          </button>
        </>
      ),
    },
  ];

  const handleToggle = async () => {
    if (!toggling) return;
    try {
      await update.mutateAsync({ id: toggling.id, data: { is_active: !toggling.is_active } });
      toast.success(toggling.is_active ? t('Vodič bol deaktivovaný') : t('Vodič bol aktivovaný'));
    } catch (err) {
      toast.error(errorMessage(err));
    }
    setToggling(null);
  };

  return (
    <>
      <PageHeader
        title={t('Vodiči')}
        subtitle={t('Zamestnanci oprávnení viesť služobné vozidlá')}
        actions={
          <Button onClick={() => setCreating(true)}>
            <Plus className="h-4 w-4" />  {t('Nový vodič')}
          </Button>
        }
      />
      <Card padding="none">
        <FilterBar>
          <SearchInput value={filters.search} onChange={(search) => setFilters({ search })} placeholder={t('Hľadať meno, os. číslo, oddelenie…')} />
          <Select
            aria-label={t('Stav')}
            wrapperClassName="sm:w-44"
            value={filters.active}
            onChange={(e) => setFilters({ active: e.target.value })}
            placeholder={t('Všetci')}
            options={[
              { value: 'true', label: t('Aktívni') },
              { value: 'false', label: t('Neaktívni') },
            ]}
          />
          <Select
            aria-label={t('Vodičský preukaz')}
            wrapperClassName="sm:w-56"
            value={filters.license}
            onChange={(e) => setFilters({ license: e.target.value })}
            placeholder={t('Ľubovoľná platnosť VP')}
            options={[{ value: 'expiring', label: t('VP neplatný / končí do 30 dní') }]}
          />
        </FilterBar>
        <DataTable
          data={data?.drivers}
          columns={columns}
          rowKey={(d) => d.id}
          isLoading={isLoading}
          isFetching={isFetching}
          rowClassName={(d) => (d.is_active ? undefined : 'text-secondary-500')}
          emptyMessage={
            <EmptyState icon={filters.license ? <CreditCard className="h-10 w-10" /> : <Users className="h-10 w-10" />} title={t('Žiadni vodiči nezodpovedajú filtru')} />
          }
          pagination={
            data && {
              page,
              pageSize,
              total: data.total,
              onPageChange: (p) => setFilters({ page: String(p) }),
              onPageSizeChange: (s) => setFilters({ page_size: String(s) }),
            }
          }
        />
      </Card>

      <DriverFormModal isOpen={creating} onClose={() => setCreating(false)} />
      <DriverFormModal isOpen={!!editing} driver={editing} onClose={() => setEditing(null)} />
      <ContractCreateModal isOpen={!!contractFor} driverId={contractFor?.id} onClose={() => setContractFor(null)} />
      <AssignmentCreateModal isOpen={!!assigningTo} driverId={assigningTo?.id} onClose={() => setAssigningTo(null)} />
      <ConfirmDialog
        isOpen={!!toggling}
        onClose={() => setToggling(null)}
        onConfirm={handleToggle}
        loading={update.isPending}
        variant={toggling?.is_active ? 'danger' : 'primary'}
        title={toggling?.is_active ? t('Deaktivovať vodiča') : t('Aktivovať vodiča')}
        confirmText={toggling?.is_active ? t('Deaktivovať') : t('Aktivovať')}
        message={
          toggling?.is_active
            ? t('Vodič {firstName} {lastName} nebude ponúkaný pri prideľovaní vozidiel. História zostane zachovaná.', { firstName: toggling.first_name, lastName: toggling.last_name })
            : t('Vodič {firstName} {lastName} bude opäť dostupný na prideľovanie vozidiel.', { firstName: toggling?.first_name, lastName: toggling?.last_name })
        }
      />
    </>
  );
}
